import React, { useCallback, useEffect, useState } from 'react';
import { humanizeEnum } from '../../utils/format';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Select,
    IconButton,
    Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import { EmployeeExitService } from '../../services/employeeExit.service';
import { FinalSettlementService } from '../../services/finalSettlement.service';
import { useToast } from '../../context/ToastContext';
import { usePayrollInstitution } from '../../hooks/usePayrollInstitution';
import { ICreateEmployeeExitRequest, IEmployeeExit, IFinalSettlement } from '../../interfaces/payroll.interface';
import EmployeeExitFormDialog from './EmployeeExitFormDialog';
import FinalSettlementPanel from './FinalSettlementPanel';
import SearchableSelect from '../common/SearchableSelect';
import { formatDate } from '../../utils/dateUtils';

// Employee exits + final settlement lifecycle (spec §2.4/§2.5/§3): exits list, create dialog, and
// a per-selected-exit settlement panel. There is no per-exit settlement GET (spec §2.4/M6 — the
// settlement DTO already embeds its exit's fields so viewers don't need the exits routes at all);
// this page instead loads the whole institution's settlements once and derives the selected
// exit's settlement client-side by matching `exitId`.
const EmployeeExitList: React.FC = () => {
    const [exits, setExits] = useState<IEmployeeExit[]>([]);
    const [settlements, setSettlements] = useState<IFinalSettlement[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [formOpen, setFormOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [selectedExitId, setSelectedExitId] = useState<string | null>(null);
    const { showSuccess, showApiError } = useToast();
    const { isSystemAdmin, institutions, institutionId, setInstitutionId } = usePayrollInstitution();

    const load = useCallback(async () => {
        if (!institutionId) return;
        setLoading(true);
        try {
            const [exitData, settlementData] = await Promise.all([
                EmployeeExitService.list(institutionId),
                FinalSettlementService.list(institutionId),
            ]);
            setExits(exitData);
            setSettlements(settlementData);
            setLoadFailed(false);
        } catch (error) {
            showApiError(error, 'Failed to load employee exits');
            setLoadFailed(true);
        } finally {
            setLoading(false);
        }
    }, [institutionId, showApiError]);

    useEffect(() => {
        load();
    }, [load]);

    const handleCreate = async (data: ICreateEmployeeExitRequest) => {
        setCreating(true);
        try {
            await EmployeeExitService.create(data, institutionId);
            showSuccess('Employee exit recorded');
            setFormOpen(false);
            load();
        } catch (error) {
            // Surface the backend's rule-naming 400 message verbatim (spec §2.4) rather than
            // predicting it client-side — the dialog stays open so the admin can adjust and retry.
            showApiError(error, 'Failed to record employee exit');
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async (exit: IEmployeeExit) => {
        try {
            await EmployeeExitService.delete(exit.id, institutionId);
            showSuccess('Employee exit deleted');
            if (selectedExitId === exit.id) setSelectedExitId(null);
            load();
        } catch (error) {
            showApiError(error, 'Failed to delete employee exit');
        }
    };

    const selectedExit = exits.find((e) => e.id === selectedExitId) || null;
    const selectedSettlement = selectedExitId
        ? settlements.find((s) => s.exitId === selectedExitId) || null
        : null;

    return (
        <Box sx={{ p: 3 }} data-help-page="payroll.exits.page">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Typography variant="h5">Employee Exits</Typography>
                <Box sx={{ flexGrow: 1 }} />
                {isSystemAdmin && (
                    <SearchableSelect
                        data-help="payroll.exits.institution"
                        label="Select Institution"
                        options={institutions}
                        value={institutionId}
                        onChange={id => setInstitutionId(id)}
                        getOptionLabel={inst => inst.name}
                        clearable={false}
                        sx={{ minWidth: 220 }}
                    />
                )}
                
                    <Button data-help="payroll.exits.new"
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setFormOpen(true)}
                        disabled={!institutionId || loadFailed}
                    >
                        New Exit
                    </Button>
                
            </Box>

            <TableContainer component={Paper} data-help-section="payroll.exits.table">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="payroll.exits.col.employee">Employee</TableCell>
                            <TableCell data-help="payroll.exits.col.type">Exit Type</TableCell>
                            <TableCell data-help="payroll.exits.col.lastDay">Last Working Day</TableCell>
                            <TableCell data-help="payroll.exits.col.notice">Notice Date</TableCell>
                            <TableCell data-help="payroll.exits.col.remarks">Remarks</TableCell>
                            <TableCell align="right" data-help="payroll.exits.col.actions">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={6} align="center">Loading...</TableCell>
                            </TableRow>
                        )}
                        {!loading && loadFailed && (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    Failed to load employee exits.
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && !loadFailed && !institutionId && (
                            <TableRow>
                                <TableCell colSpan={6} align="center">Select an institution to view its employee exits</TableCell>
                            </TableRow>
                        )}
                        {!loading && !loadFailed && institutionId && exits.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} align="center">No employee exits found</TableCell>
                            </TableRow>
                        )}
                        {!loading && !loadFailed && exits.map((exit) => {
                            const settlement = settlements.find((s) => s.exitId === exit.id);
                            const canDeleteExit = !settlement
                                || (settlement.status !== 'FINALIZED' && settlement.status !== 'PAID');
                            return (
                                <TableRow
                                    key={exit.id}
                                    selected={selectedExitId === exit.id}
                                    hover
                                >
                                    <TableCell>{exit.employeeName || exit.username || exit.userId}</TableCell>
                                    <TableCell>{humanizeEnum(exit.exitType)}</TableCell>
                                    <TableCell>{formatDate(exit.lastWorkingDay)}</TableCell>
                                    <TableCell>{exit.noticeDate ? formatDate(exit.noticeDate) : '—'}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'normal', minWidth: 140 }}>{exit.remarks || '—'}</TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Manage settlement">
                                            <IconButton
                                                data-help="payroll.exits.action.manage"
                                                size="small"
                                                aria-label={`Manage settlement for ${exit.employeeName || exit.username || exit.userId}`}
                                                onClick={() => setSelectedExitId(exit.id)}
                                            >
                                                <SettingsIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        {canDeleteExit && (
                                            <Tooltip title="Delete exit">
                                                <IconButton
                                                    data-help="payroll.exits.action.delete"
                                                    size="small"
                                                    aria-label={`Delete exit for ${exit.employeeName || exit.username || exit.userId}`}
                                                    onClick={() => handleDelete(exit)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {selectedExit && (
                <FinalSettlementPanel
                    exit={selectedExit}
                    settlement={selectedSettlement}
                    institutionId={institutionId}
                    onChange={load}
                />
            )}

            <EmployeeExitFormDialog
                open={formOpen}
                onClose={() => !creating && setFormOpen(false)}
                onSave={handleCreate}
                saving={creating}
            />
        </Box>
    );
};

export default EmployeeExitList;
